import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import EditIcon from '../../components/Table/EditIcon/EditIcon';
import Filter from '../../components/common/Filter/Filter';
import AddButton from '../../components/common/AddButton/AddButton';
import SimpleSearchBox from '../../components/common/Search/SimpleSearch';
import Sort from '../../components/common/Sort/Sort';
import DeleteIcon from '../../components/Table/DeleteIcon/DeleteIcon';
import DeletePopup from '../../components/Popup/DeletePopup/DeletePopup';
import '../../components/common/SubNavbar.css';
import '../../components/common/CommonText.css';
import '../../components/common/Table.css';
import './FinancialYear.css';
import { fetchFinancialYears, deleteFinancialYear } from '../../services/Localization/FinancialYearService';
import { useCheckboxClick } from '../../checkboxclick';
import useFilterStore from '../../store/useFilterStore';

const pageKey = "financial_year";

const FinancialYearList = () => {
  const { setFilters, getFilters } = useFilterStore();
  const savedFilters = getFilters(pageKey);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const sortOptions = ["A-Z", "Z-A", "Newest", "Oldest"];
  const filterOptions = ["Default", "Archived", "Unarchived"];
  const [searchParams, setSearchParams] = useState(savedFilters?.searchParams || { term: "" });
  const [selectedFilterOption, setSelectedFilterOption] = useState(savedFilters?.selectedFilterOption || "Default");
  const [selectedSortOption, setSelectedSortOption] = useState(savedFilters?.selectedSortOption || "Newest");

  const { isParentSelected, selectedItems, handleParentCheckboxChange, handleChildCheckboxChange } = useCheckboxClick(items);
  const navigate = useNavigate();

  const mapSortOptionToQuery = (option) => {
    switch (option) {
      case 'A-Z': return 'a_to_z';
      case 'Z-A': return 'z_to_a';
      case 'Oldest': return 'oldest';
      case 'Newest': default: return 'newest';
    }
  };

  const loadFinancialYears = async () => {
    try {
      const params = {
        search: searchParams.term || undefined,
        filter: selectedFilterOption === "Default" ? undefined : selectedFilterOption.toLowerCase(),
        sort: mapSortOptionToQuery(selectedSortOption),
      };

      const res = await fetchFinancialYears(params);
      if (res && res.response_code === 1) {
        setItems(res.data || []);
      }
    } catch (error) {
      if (error?.response?.data?.error_code === 1001) {
        navigate("/permission-denied");
      } else {
        console.error("Error loading financial years:", error);
      }
    }
  };

  useEffect(() => {
    loadFinancialYears();
    setFilters(pageKey, {
      searchParams,
      selectedFilterOption,
      selectedSortOption,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, selectedFilterOption, selectedSortOption]);

  const handleSearchSelect = (term) => {
    setSearchParams({ term });
  };

  const handleFilterSelect = (option) => {
    setSelectedFilterOption(option);
  };

  const handleSortOptionSelect = (option) => {
    setSelectedSortOption(option);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setIsDeletePopupOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedItem) return;
    try {
      await deleteFinancialYear(selectedItem.id);
      setIsDeletePopupOpen(false);
      setSelectedItem(null);
      loadFinancialYears();
    } catch (error) {
      console.error("Failed to delete financial year:", error);
    }
  };

  const renderCountriesColumn = (countries = []) => {
    if (!countries || countries.length === 0) return "—";
    const maxVisible = 8;
    const remainingCount = countries.length - maxVisible;

    if (remainingCount <= 0) {
      return countries.map(c => c.name).join(", ");
    }

    const visibleCountries = countries.slice(0, maxVisible).map(c => c.name).join(", ");

    return (
      <div className="fy-table-country-cell">
        <span>{visibleCountries}</span>
        <span className="fy-badge-count">+{remainingCount}</span>
      </div>
    );
  };

  return (
    <div className='whole-container'>
      <div className='top-heading'>
        <PageHeader pageName="Localization /" nestedpageName="Financial Year" linkTo="" />
      </div>

      <div className='pagename-container'>
        <div className='page-name'>
          <h1 className='common-text fts-22 ftw-6 blue'>Financial Years</h1>
        </div>
        <div className='pagename-right-container'>
          <AddButton onClick={() => navigate('/financial-year/add')} />
          <SimpleSearchBox placeholder="Search" onSearchSelect={handleSearchSelect} defaultValue={searchParams?.term} />
          <Filter options={filterOptions} onFilterSelect={handleFilterSelect} defaultValue={selectedFilterOption} />
          <Sort options={sortOptions} onOptionSelect={handleSortOptionSelect} defaultValue={selectedSortOption} />
        </div>
      </div>

      <div className='table-container'>
        <table className='common-table'>
          <thead className='common-table-head'>
            <tr className='common-table-thead-tr'>
              <th>
                <input
                  className="table-selectbox table-parent-selectbox"
                  type="checkbox"
                  checked={isParentSelected}
                  onChange={handleParentCheckboxChange}
                />
                START DATE
              </th>
              <th>END DATE</th>
              <th>COUNTRIES</th>
              <th className='for-hide'></th>
            </tr>
          </thead>
          <tbody className='common-table-tbody'>
            {items.length > 0 ? (
              items.map((item) => (
                <tr key={item.id} className={item.is_archived ? 'archived-item' : ''}>
                  <td className='country-label'>
                    <input
                      className="table-selectbox table-child-selectbox"
                      type="checkbox"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => handleChildCheckboxChange(item.id)}
                    />
                    <span>{item.start_date || `${item.start_month}/${item.start_day}`}</span>
                  </td>
                  <td>{item.end_date || `${item.end_month}/${item.end_day}`}</td>
                  <td>{renderCountriesColumn(item.countries)}</td>
                  <td className='for-hide'>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <svg
                        className="cr-pointer"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        onClick={() => navigate(`/financial-year/detail/${item.id}`)}
                      >
                        <path d="M10 4.5C4.5 4.5 1 10 1 10C1 10 4.5 15.5 10 15.5C15.5 15.5 19 10 19 10C19 10 15.5 4.5 10 4.5Z" stroke="#094C80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#094C80" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <EditIcon onClick={() => navigate(`/financial-year/edit/${item.id}`)} />
                      <DeleteIcon onClick={() => handleDeleteClick(item)} />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                  No financial years found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isDeletePopupOpen && (
        <DeletePopup
          isOpen={isDeletePopupOpen}
          onClose={() => setIsDeletePopupOpen(false)}
          onSave={confirmDelete}
        />
      )}
    </div>
  );
};

export default FinancialYearList;
